/**
 * Shared materials and procedural textures.
 *
 * Everything is generated in code: no external art assets, so there is no
 * licence risk and no binary blobs in the repository (docs/ASSET_LICENSES.md).
 * The look we are after is stylised semi-realistic — legible first, pretty
 * second — which procedural geometry and generated textures serve well.
 */
import * as THREE from 'three';

const cache = new Map<string, THREE.Material>();
const textureCache = new Map<string, THREE.Texture>();

function remember<T extends THREE.Material>(key: string, make: () => T): T {
  const found = cache.get(key);
  if (found) return found as T;
  const made = make();
  cache.set(key, made);
  return made;
}

export function standard(colour: number, opts: { rough?: number; metal?: number } = {}) {
  const key = `std:${colour}:${opts.rough ?? 0.9}:${opts.metal ?? 0}`;
  return remember(key, () =>
    new THREE.MeshStandardMaterial({
      color: colour,
      roughness: opts.rough ?? 0.9,
      metalness: opts.metal ?? 0,
    }),
  );
}

function canvas(size: number): { c: HTMLCanvasElement; ctx: CanvasRenderingContext2D } {
  const c = document.createElement('canvas');
  c.width = c.height = size;
  return { c, ctx: c.getContext('2d')! };
}

/**
 * Scatter single-pixel grain across a canvas in one pass.
 *
 * The straightforward version of this is a loop of `ctx.fillRect(x, y, 1, 1)`,
 * which is what it used to be. At the densities this art style wants that is a
 * quarter of a million canvas calls at load, each one going through the 2D
 * context's state machine — several seconds on a machine without much CPU to
 * spare, and the largest single contributor to load time. Writing the pixels
 * straight into an ImageData buffer produces the same image in milliseconds.
 *
 * @param colours hex strings, picked from at random per speck
 * @param density specks per pixel of canvas area
 * @param alpha   0..1, blended against what is already there
 * @param tall    fraction of specks that are two pixels high rather than one
 */
function sprinkle(
  ctx: CanvasRenderingContext2D,
  size: number,
  colours: string[],
  density: number,
  alpha = 1,
  tall = 0,
): void {
  const image = ctx.getImageData(0, 0, size, size);
  const data = image.data;
  const rgb = colours.map((colour) => {
    const hex = colour.replace('#', '');
    return [
      parseInt(hex.slice(0, 2), 16),
      parseInt(hex.slice(2, 4), 16),
      parseInt(hex.slice(4, 6), 16),
    ] as const;
  });

  const blend = (x: number, y: number, c: readonly [number, number, number]) => {
    const i = ((y % size) * size + (x % size)) * 4;
    data[i] = data[i]! + (c[0] - data[i]!) * alpha;
    data[i + 1] = data[i + 1]! + (c[1] - data[i + 1]!) * alpha;
    data[i + 2] = data[i + 2]! + (c[2] - data[i + 2]!) * alpha;
  };

  const count = Math.round(size * size * density);
  for (let n = 0; n < count; n++) {
    const x = (Math.random() * size) | 0;
    const y = (Math.random() * size) | 0;
    const colour = rgb[(Math.random() * rgb.length) | 0]!;
    blend(x, y, colour);
    if (tall > 0 && Math.random() < tall) blend(x, y + 1, colour);
  }
  ctx.putImageData(image, 0, 0);
}

/**
 * Derive a normal map from a colour texture's own luminance.
 *
 * VISUAL_REALISM_AUDIT R-03: there was not one normal map in the project.
 * Siding, tarmac, paving and shingle were painted patterns on flat planes -
 * they did not catch light at a grazing angle and looked identical from every
 * direction. That, together with the missing ambient occlusion, is most of why
 * surfaces read as printed rather than as made of something.
 *
 * The textures are all generated here, in code, so their luminance is already a
 * good height field: the mortar lines drawn darker than the brick, the gaps
 * between siding boards, the aggregate grain in tarmac. A Sobel gradient over
 * that luminance turns each one into relief for free - no authored maps, no
 * external assets, nothing added to the licence register.
 *
 * @param strength height scale; higher exaggerates the relief
 */
function normalFromCanvas(
  source: HTMLCanvasElement,
  strength = 1,
): THREE.CanvasTexture {
  const size = source.width;
  const src = source.getContext('2d')!.getImageData(0, 0, size, source.height);
  const height = source.height;
  const out = document.createElement('canvas');
  out.width = size;
  out.height = height;
  const dst = out.getContext('2d')!.createImageData(size, height);

  const raw = new Float32Array(size * height);
  for (let i = 0; i < raw.length; i++) {
    raw[i] =
      (0.2126 * src.data[i * 4]! +
        0.7152 * src.data[i * 4 + 1]! +
        0.0722 * src.data[i * 4 + 2]!) / 255;
  }

  // High-pass before taking the gradient.
  //
  // Deriving relief from colour has one classic failure: *tonal* variation gets
  // read as *height* variation. These textures carry broad soft patches of
  // lighter and darker paint to stop large surfaces looking flat, and taken
  // literally as a height field those patches become physical dents - the first
  // attempt at this turned a painted wall into hammered metal.
  //
  // Subtracting a blurred copy leaves only the sharp detail: the shadow line
  // between two siding boards, a mortar joint, a slab edge, aggregate grain.
  // Those are the things that really are relief.
  const blurred = new Float32Array(size * height);
  const radius = Math.max(2, Math.round(size / 48));
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < size; x++) {
      let total = 0;
      let count = 0;
      for (let oy = -radius; oy <= radius; oy += 2) {
        for (let ox = -radius; ox <= radius; ox += 2) {
          total += raw[((y + oy + height) % height) * size + ((x + ox + size) % size)]!;
          count++;
        }
      }
      blurred[y * size + x] = total / count;
    }
  }
  const lum = new Float32Array(size * height);
  for (let i = 0; i < lum.length; i++) lum[i] = 0.5 + (raw[i]! - blurred[i]!);
  // Wrapping lookups: these textures tile, so the normal map has to tile too or
  // every repeat boundary becomes a visible seam.
  const at = (x: number, y: number): number =>
    lum[((y + height) % height) * size + ((x + size) % size)]!;

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < size; x++) {
      const dx =
        at(x - 1, y - 1) + 2 * at(x - 1, y) + at(x - 1, y + 1) -
        at(x + 1, y - 1) - 2 * at(x + 1, y) - at(x + 1, y + 1);
      const dy =
        at(x - 1, y - 1) + 2 * at(x, y - 1) + at(x + 1, y - 1) -
        at(x - 1, y + 1) - 2 * at(x, y + 1) - at(x + 1, y + 1);
      const nx = dx * strength;
      const ny = dy * strength;
      const nz = 1;
      const length = Math.hypot(nx, ny, nz);
      const i = (y * size + x) * 4;
      dst.data[i] = ((nx / length) * 0.5 + 0.5) * 255;
      dst.data[i + 1] = ((ny / length) * 0.5 + 0.5) * 255;
      dst.data[i + 2] = ((nz / length) * 0.5 + 0.5) * 255;
      dst.data[i + 3] = 255;
    }
  }
  out.getContext('2d')!.putImageData(dst, 0, 0);
  const texture = new THREE.CanvasTexture(out);
  texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
  texture.anisotropy = 16;
  return texture;
}

/**
 * A roughness map from the same luminance.
 *
 * Real surfaces are not uniformly rough: mortar is duller than brick, the worn
 * centre of a paving slab is smoother than its edges, tarmac aggregate scatters
 * more than the bitumen around it. Mapping luminance onto a narrow roughness
 * range gives that variation from the texture that already exists, and it is
 * what makes the environment map from R-02 produce a varied specular response
 * instead of a uniform sheen.
 */
function roughnessFromCanvas(
  source: HTMLCanvasElement,
  low: number,
  high: number,
): THREE.CanvasTexture {
  const size = source.width;
  const height = source.height;
  const src = source.getContext('2d')!.getImageData(0, 0, size, height);
  const out = document.createElement('canvas');
  out.width = size;
  out.height = height;
  const dst = out.getContext('2d')!.createImageData(size, height);
  for (let i = 0; i < size * height; i++) {
    const l =
      (0.2126 * src.data[i * 4]! +
        0.7152 * src.data[i * 4 + 1]! +
        0.0722 * src.data[i * 4 + 2]!) / 255;
    // Roughness lives in the green channel for a MeshStandardMaterial.
    const value = (low + (high - low) * l) * 255;
    dst.data[i * 4] = 0;
    dst.data[i * 4 + 1] = value;
    dst.data[i * 4 + 2] = 0;
    dst.data[i * 4 + 3] = 255;
  }
  out.getContext('2d')!.putImageData(dst, 0, 0);
  const texture = new THREE.CanvasTexture(out);
  texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
  texture.anisotropy = 16;
  return texture;
}

/**
 * Give a material relief and roughness variation derived from its own colour
 * canvas, and keep every map on the same tiling as the colour map.
 */
function addSurfaceDetail(
  material: THREE.MeshStandardMaterial,
  source: HTMLCanvasElement,
  opts: {
    normal?: number; rough?: [number, number]; scale?: number;
    /**
     * Cache key for the derived maps.
     *
     * Relief does not depend on paint colour: siding has the same shadow line
     * under every board whether the house is cream or blue, and shingle has the
     * same profile on every roof. The colour materials are cached per colour -
     * seventeen wall colours, a dozen roof colours - so deriving the maps
     * alongside them produced seventeen identical normal maps. Keyed on the
     * pattern instead, there is one.
     */
    key?: string;
  } = {},
): THREE.MeshStandardMaterial {
  const map = material.map;
  const cached = <T extends THREE.Texture>(name: string, make: () => T): T => {
    if (!opts.key) return make();
    const found = textureCache.get(`${opts.key}:${name}`);
    if (found) return found as T;
    const made = make();
    textureCache.set(`${opts.key}:${name}`, made);
    return made;
  };

  if (opts.normal !== undefined) {
    const normal = cached('n', () => normalFromCanvas(source, opts.normal!));
    if (map) normal.repeat.copy(map.repeat);
    material.normalMap = normal;
    material.normalScale = new THREE.Vector2(
      opts.scale ?? 1, opts.scale ?? 1,
    );
  }
  if (opts.rough) {
    const rough = cached('r', () =>
      roughnessFromCanvas(source, opts.rough![0], opts.rough![1]));
    if (map) rough.repeat.copy(map.repeat);
    material.roughnessMap = rough;
    // The map multiplies the scalar, so the scalar has to be 1 or the range
    // collapses toward zero.
    material.roughness = 1;
  }
  material.needsUpdate = true;
  return material;
}

/**
 * Pull a hex colour toward a realistic outdoor range.
 *
 * VISUAL_REALISM_AUDIT R-07: the palette was saturated and evenly valued. Real
 * outdoor scenes are far less saturated than intuition suggests - grass in
 * daylight sits nearer olive than emerald, and tarmac carries a blue-brown cast
 * rather than neutral grey. Rather than hand-editing sixty hex literals and
 * losing the relationships between them, every palette entry goes through this,
 * so the whole scheme moves together and the numbers in the source still read
 * as the colours the artist meant.
 *
 * @param amount 0 keeps the colour, 1 takes it to grey
 * @param warmth positive shifts the desaturated result toward earth, negative
 *               toward slate
 */
function grade(hex: string, amount = 0.22, warmth = 0.04): string {
  const value = parseInt(hex.replace('#', ''), 16);
  let r = (value >> 16) & 255;
  let g = (value >> 8) & 255;
  let b = value & 255;
  const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  r = r + (luma - r) * amount;
  g = g + (luma - g) * amount;
  b = b + (luma - b) * amount;
  r += luma * warmth;
  g += luma * warmth * 0.4;
  b -= luma * warmth * 0.6;
  const clamp = (v: number): number => Math.max(0, Math.min(255, Math.round(v)));
  return `#${((clamp(r) << 16) | (clamp(g) << 8) | clamp(b))
    .toString(16)
    .padStart(6, '0')}`;
}

/** Grade a whole palette in one call. */
function graded(palette: string[], amount?: number, warmth?: number): string[] {
  return palette.map((c) => grade(c, amount, warmth));
}

/**
 * Darken the outer band of a tiling texture.
 *
 * VISUAL_REALISM_AUDIT R-10: nothing in the world was dirty. Every junction -
 * lawn to pavement, kerb to road, wall to ground - met at a clean line, and
 * perfection reads as synthetic. Real surfaces collect grime at their edges
 * where water sits and feet do not reach.
 *
 * Applied inside the texture rather than as a decal, so it costs nothing at
 * render time and tiles with everything else.
 */
function grimeEdges(
  ctx: CanvasRenderingContext2D,
  size: number,
  strength: number,
): void {
  const band = Math.round(size * 0.16);
  for (const [x, y, w, h] of [
    [0, 0, size, band], [0, size - band, size, band],
    [0, 0, band, size], [size - band, 0, band, size],
  ] as [number, number, number, number][]) {
    const horizontal = w > h;
    const gradient = horizontal
      ? ctx.createLinearGradient(0, y, 0, y + h)
      : ctx.createLinearGradient(x, 0, x + w, 0);
    const near = `rgba(38,32,26,${strength})`;
    const far = 'rgba(38,32,26,0)';
    const atStart = y === 0 && x === 0
      ? true
      : (horizontal ? y === 0 : x === 0);
    gradient.addColorStop(0, atStart ? near : far);
    gradient.addColorStop(1, atStart ? far : near);
    ctx.fillStyle = gradient;
    ctx.fillRect(x, y, w, h);
  }
}

/**
 * Foliage: a cluster of leaves on transparent ground, for alpha-tested cards.
 *
 * VISUAL_REALISM_AUDIT R-05: canopies were two or three solid icosahedra of one
 * flat green. Foliage is a large fraction of the pixels in most outdoor frames,
 * so a coloured lump where a tree should be does more damage than its object
 * count suggests. What is wrong with it is the silhouette - a smooth convex
 * shell against the sky is the one shape a tree never has.
 *
 * Alpha-tested cards fix the silhouette, and because every card in the
 * neighbourhood shares this one material they merge through MeshBatcher into a
 * single draw call. That is what makes the technique affordable here.
 *
 * Vertex colours carry the per-canopy and per-card tint, so one material still
 * covers a whole neighbourhood of trees in different greens.
 */
export function leafMaterial(): THREE.MeshStandardMaterial {
  return remember('leaf', () => {
    const size = 256;
    const { c, ctx } = canvas(size);
    ctx.clearRect(0, 0, size, size);

    // Leaves are drawn white and tinted at render time by the vertex colour, so
    // one texture serves every species and season.
    const leaf = (x: number, y: number, r: number, angle: number, alpha: number) => {
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(angle);
      ctx.globalAlpha = alpha;
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      // A pointed ellipse reads as a leaf where a circle reads as a dot.
      ctx.moveTo(0, -r);
      ctx.quadraticCurveTo(r * 0.75, -r * 0.15, 0, r);
      ctx.quadraticCurveTo(-r * 0.75, -r * 0.15, 0, -r);
      ctx.fill();
      ctx.restore();
    };

    // Dense in the middle, thinning toward the edge, so a card's own outline is
    // ragged and the mass reads as depth rather than as a decal.
    for (let i = 0; i < 240; i++) {
      const angle = Math.random() * Math.PI * 2;
      const spread = Math.pow(Math.random(), 0.55) * size * 0.46;
      const x = size / 2 + Math.cos(angle) * spread;
      const y = size / 2 + Math.sin(angle) * spread;
      const edge = spread / (size * 0.46);
      leaf(
        x, y,
        size * (0.055 + Math.random() * 0.045),
        Math.random() * Math.PI * 2,
        0.55 + (1 - edge) * 0.45,
      );
    }
    ctx.globalAlpha = 1;

    const texture = new THREE.CanvasTexture(c);
    texture.anisotropy = 16;

    return new THREE.MeshStandardMaterial({
      // Alpha only, no colour map. Using the same canvas for both was the first
      // attempt and it rendered canopies near-black: compositing dozens of
      // part-transparent leaf shapes onto a transparent canvas darkens the
      // stored RGB, so the "white" leaves were not white. The colour comes
      // entirely from the vertex tint instead, which is where the per-canopy
      // and top-to-underside variation already lives.
      alphaMap: texture,
      transparent: false,
      // Alpha *test* rather than blend: blended foliage needs sorting, and
      // unsorted blended cards inside one merged mesh flicker against each
      // other. A test also lets the card cast a real shaped shadow.
      alphaTest: 0.42,
      side: THREE.DoubleSide,
      roughness: 0.86,
      vertexColors: true,
    });
  });
}

/** Noise keeps large flat surfaces from looking like plastic. */
function noiseTexture(
  base: string, speckle: string, size = 128, density = 0.35, blobs = 0,
): THREE.Texture {
  const { c, ctx } = canvas(size);
  ctx.fillStyle = base;
  ctx.fillRect(0, 0, size, size);

  // Soft patches first, then fine grain on top. Two scales of variation read as
  // a surface; one scale reads as static.
  for (let i = 0; i < blobs; i++) {
    const x = Math.random() * size;
    const y = Math.random() * size;
    const r = size * (0.05 + Math.random() * 0.12);
    const gradient = ctx.createRadialGradient(x, y, 0, x, y, r);
    gradient.addColorStop(0, speckle);
    gradient.addColorStop(1, 'transparent');
    ctx.globalAlpha = 0.25;
    ctx.fillStyle = gradient;
    ctx.fillRect(x - r, y - r, r * 2, r * 2);
  }
  ctx.globalAlpha = 1;

  sprinkle(ctx, size, [speckle], density);
  const texture = new THREE.CanvasTexture(c);
  texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
  texture.anisotropy = 16;
  return texture;
}

/**
 * Ground grass.
 *
 * The largest surface in almost every frame, and it used to be one flat
 * saturated green. Three scales of variation now: broad tonal patches, mid
 * clumps, and fine grain - which is what stops a big plane reading as paint.
 */
export function grassMaterial(): THREE.MeshStandardMaterial {
  return remember('grass', () => {
    const tex = layeredGround(
      graded(['#4f7d3a', '#5d8c44', '#446f34', '#688f4a', '#3f6830'], 0.3, 0.05),
      512,
    );
    tex.repeat.set(26, 26);
    return addSurfaceDetail(
      new THREE.MeshStandardMaterial({ map: tex }),
      tex.image as HTMLCanvasElement,
      // Deliberately the weakest detail in the set. Grass tiles 26 times across
      // the ground plane, and a roughness map with any real range makes the
      // environment map pick out an identical specular response on every tile -
      // which turns a lawn into a visible checkerboard. Narrow range, gentle
      // normal: enough to stop it being a flat plane, not enough to advertise
      // the repeat.
      { normal: 0.5, scale: 0.18, rough: [0.95, 1] },
    );
  });
}

/**
 * A ground texture built from overlapping soft blobs of several related tones
 * plus fine grain. Cheap, tiles well, and reads as ground rather than as a
 * colour swatch at any distance.
 */
function layeredGround(palette: string[], size: number): THREE.Texture {
  const { c, ctx } = canvas(size);
  ctx.fillStyle = palette[0]!;
  ctx.fillRect(0, 0, size, size);

  // Broad patches, then smaller clumps on top.
  for (const [count, scale, alpha] of [[26, 0.30, 0.5], [90, 0.09, 0.4], [220, 0.03, 0.35]] as const) {
    for (let i = 0; i < count; i++) {
      const x = Math.random() * size;
      const y = Math.random() * size;
      const r = size * scale * (0.5 + Math.random());
      const gradient = ctx.createRadialGradient(x, y, 0, x, y, r);
      const colour = palette[1 + Math.floor(Math.random() * (palette.length - 1))]!;
      gradient.addColorStop(0, colour);
      gradient.addColorStop(1, 'transparent');
      ctx.globalAlpha = alpha;
      ctx.fillStyle = gradient;
      ctx.fillRect(x - r, y - r, r * 2, r * 2);
    }
  }
  ctx.globalAlpha = 1;

  // Fine grain, in both directions so it does not read as a scan pattern.
  sprinkle(ctx, size, palette, 0.35, 0.35, 0.5);

  const texture = new THREE.CanvasTexture(c);
  texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
  texture.anisotropy = 16;
  return texture;
}

/** A lawn tone for a specific upkeep level, from neglected to immaculate. */
export function lawnMaterial(upkeep: number): THREE.MeshStandardMaterial {
  const step = Math.round(Math.max(0, Math.min(1, upkeep)) * 4);
  return remember(`lawn:${step}`, () => {
    const palettes = [
      ['#8a8a4a', '#9a9455', '#7c7a42', '#a09a5f', '#6f6d3c'],
      ['#7d8a45', '#8a9450', '#6f7c3e', '#95a05a', '#647038'],
      ['#5f8a40', '#6d9449', '#547c38', '#77a052', '#4a7033'],
      ['#4e8a3c', '#5b9445', '#457c34', '#66a04c', '#3d7030'],
      ['#3f8a36', '#4a9440', '#387c30', '#55a045', '#317028'],
    ];
    const tex = layeredGround(graded(palettes[step] ?? palettes[2]!, 0.26, 0.05), 256);
    tex.repeat.set(6, 6);
    return addSurfaceDetail(
      new THREE.MeshStandardMaterial({ map: tex }),
      tex.image as HTMLCanvasElement,
      { normal: 0.7, scale: 0.3, rough: [0.92, 1] },
    );
  });
}

export function roadMaterial(): THREE.MeshStandardMaterial {
  return remember('road', () => {
    const size = 256;
    const { c, ctx } = canvas(size);
    ctx.fillStyle = grade('#3a3d43', 0.1, 0.05);
    ctx.fillRect(0, 0, size, size);
    // Patches of older and newer tarmac, then aggregate grain and a few cracks.
    for (let i = 0; i < 30; i++) {
      const x = Math.random() * size;
      const y = Math.random() * size;
      const r = size * (0.06 + Math.random() * 0.18);
      const gradient = ctx.createRadialGradient(x, y, 0, x, y, r);
      gradient.addColorStop(0, Math.random() < 0.5 ? grade('#43464d', 0.1, 0.05) : grade('#33363b', 0.1, 0.05));
      gradient.addColorStop(1, 'transparent');
      ctx.globalAlpha = 0.5;
      ctx.fillStyle = gradient;
      ctx.fillRect(x - r, y - r, r * 2, r * 2);
    }
    ctx.globalAlpha = 1;
    sprinkle(ctx, size, graded(['#4b4e56', '#31343a'], 0.1, 0.05), 0.5);
    ctx.strokeStyle = 'rgba(28,30,34,0.55)';
    ctx.lineWidth = 1.4;
    for (let i = 0; i < 7; i++) {
      ctx.beginPath();
      let x = Math.random() * size;
      let y = Math.random() * size;
      ctx.moveTo(x, y);
      for (let j = 0; j < 5; j++) {
        x += (Math.random() - 0.5) * 60;
        y += (Math.random() - 0.5) * 60;
        ctx.lineTo(x, y);
      }
      ctx.stroke();
    }
    // Grime toward the kerb, where water sits and nothing scrubs it.
    grimeEdges(ctx, size, 0.16);
    const tex = new THREE.CanvasTexture(c);
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    tex.repeat.set(9, 9);
    tex.anisotropy = 16;
    // Tarmac: aggregate scatters light, the bitumen around it does not, so the
    // roughness range here is the widest in the game.
    return addSurfaceDetail(
      new THREE.MeshStandardMaterial({ map: tex }),
      tex.image as HTMLCanvasElement,
      { normal: 1.4, scale: 0.75, rough: [0.62, 0.99] },
    );
  });
}

export function pavementMaterial(): THREE.MeshStandardMaterial {
  return remember('pavement', () => {
    // Paving slabs: a grid drawn once and tiled.
    const { c, ctx } = canvas(128);
    ctx.fillStyle = grade('#93918b', 0.16, 0.05);
    ctx.fillRect(0, 0, 128, 128);
    sprinkle(ctx, 128, [grade('#8b8983', 0.16, 0.05)], 0.3);
    // Slab joints, plus a lighter highlight on one edge of each slab so the
    // paving reads as laid rather than printed.
    ctx.strokeStyle = 'rgba(56,56,54,0.5)';
    ctx.lineWidth = 2.5;
    for (const p of [0, 64, 128]) {
      ctx.beginPath(); ctx.moveTo(p, 0); ctx.lineTo(p, 128); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(0, p); ctx.lineTo(128, p); ctx.stroke();
    }
    ctx.strokeStyle = 'rgba(176,174,168,0.55)';
    ctx.lineWidth = 1.5;
    for (const p of [2, 66]) {
      ctx.beginPath(); ctx.moveTo(p, 0); ctx.lineTo(p, 128); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(0, p); ctx.lineTo(128, p); ctx.stroke();
    }
    // A little staining so every slab is not identical.
    for (let i = 0; i < 16; i++) {
      const x = Math.random() * 128;
      const y = Math.random() * 128;
      const r = 6 + Math.random() * 16;
      const gradient = ctx.createRadialGradient(x, y, 0, x, y, r);
      gradient.addColorStop(0, 'rgba(120,118,112,0.35)');
      gradient.addColorStop(1, 'transparent');
      ctx.fillStyle = gradient;
      ctx.fillRect(x - r, y - r, r * 2, r * 2);
    }
    // Slabs collect dirt along their joints far more than across their faces.
    grimeEdges(ctx, 128, 0.2);
    const tex = new THREE.CanvasTexture(c);
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    tex.repeat.set(4, 4);
    tex.anisotropy = 16;
    return addSurfaceDetail(
      new THREE.MeshStandardMaterial({ map: tex }),
      tex.image as HTMLCanvasElement,
      { normal: 1.5, scale: 0.85, rough: [0.6, 0.96] },
    );
  });
}

/**
 * A wall surface with faint horizontal siding lines. Flat-coloured walls are
 * the main reason the buildings still read as slabs at a distance.
 */
export function wallMaterial(colour: number, siding = true): THREE.MeshStandardMaterial {
  const key = `wall:${colour}:${siding}`;
  return remember(key, () => {
    const size = 128;
    const { c, ctx } = canvas(size);
    const base = new THREE.Color(colour);
    ctx.fillStyle = `#${base.getHexString()}`;
    ctx.fillRect(0, 0, size, size);

    if (siding) {
      const dark = base.clone().offsetHSL(0, 0, -0.05);
      const light = base.clone().offsetHSL(0, 0, 0.035);
      for (let y = 0; y < size; y += 16) {
        ctx.fillStyle = `#${dark.getHexString()}`;
        ctx.fillRect(0, y, size, 1.5);
        ctx.fillStyle = `#${light.getHexString()}`;
        ctx.fillRect(0, y + 2, size, 1);
      }
    }
    // Weathering, so no two square metres are identical.
    for (let i = 0; i < 26; i++) {
      const x = Math.random() * size;
      const y = Math.random() * size;
      const r = 6 + Math.random() * 22;
      const gradient = ctx.createRadialGradient(x, y, 0, x, y, r);
      const tone = base.clone().offsetHSL(0, 0, (Math.random() - 0.5) * 0.06);
      gradient.addColorStop(0, `#${tone.getHexString()}`);
      gradient.addColorStop(1, 'transparent');
      ctx.globalAlpha = 0.5;
      ctx.fillStyle = gradient;
      ctx.fillRect(x - r, y - r, r * 2, r * 2);
    }
    ctx.globalAlpha = 1;

    const tex = new THREE.CanvasTexture(c);
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    tex.repeat.set(2.5, 2.5);
    tex.anisotropy = 16;
    // Siding: the shadow line under each board is the whole point of the
    // relief here, so this one gets the strongest normal in the set.
    return addSurfaceDetail(
      new THREE.MeshStandardMaterial({ map: tex }),
      tex.image as HTMLCanvasElement,
      { normal: 1.8, scale: 0.7, rough: [0.56, 0.94], key: `wall:${siding}` },
    );
  });
}

/** Roof surface with shingle rows. */
export function roofMaterial(colour: number): THREE.MeshStandardMaterial {
  return remember(`roof:${colour}`, () => {
    const size = 128;
    const { c, ctx } = canvas(size);
    const base = new THREE.Color(colour);
    ctx.fillStyle = `#${base.getHexString()}`;
    ctx.fillRect(0, 0, size, size);
    const dark = base.clone().offsetHSL(0, 0, -0.07);
    const light = base.clone().offsetHSL(0, 0, 0.04);
    for (let y = 0; y < size; y += 11) {
      ctx.fillStyle = `#${dark.getHexString()}`;
      ctx.fillRect(0, y, size, 2);
      const offset = (y / 11) % 2 === 0 ? 0 : 11;
      for (let x = offset; x < size; x += 22) {
        ctx.fillStyle = `#${light.getHexString()}`;
        ctx.fillRect(x, y + 2, 1.5, 9);
      }
    }
    const tex = new THREE.CanvasTexture(c);
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    tex.repeat.set(4, 4);
    tex.anisotropy = 16;
    return addSurfaceDetail(
      new THREE.MeshStandardMaterial({ map: tex }),
      tex.image as HTMLCanvasElement,
      { normal: 1.6, scale: 0.8, rough: [0.6, 0.97], key: 'roof' },
    );
  });
}

/**
 * Wet the ground down in the rain.
 *
 * Tarmac and paving go two shades darker and much glossier when it rains, and
 * that reads as rain far more strongly than the falling streaks do - a dry
 * road under a downpour is the thing that looks wrong in a game, not the drops
 * themselves. Only the three shared ground materials are touched; grass and
 * lawns are per-upkeep and stay as they are.
 *
 * @param wetness 0 = dry, 1 = raining
 */
export function setGroundWetness(wetness: number): void {
  const wet = Math.max(0, Math.min(1, wetness));
  for (const [key, dryRoughness] of [
    // Road and pavement carry a roughness map now, and the scalar multiplies
    // it, so their dry value is 1: wetness scales the whole map down together
    // rather than flattening it to one number.
    ['road', 1], ['pavement', 1], ['kerb', 0.9],
  ] as [string, number][]) {
    const material = cache.get(key) as THREE.MeshStandardMaterial | undefined;
    if (!material) continue;
    // The materials carry a texture map, so this colour multiplies it. Note the
    // values are linear, not sRGB: 0.6 linear is only about 20% darker to the
    // eye, which is why the first attempt at this was invisible.
    material.color.setRGB(1 - wet * 0.66, 1 - wet * 0.64, 1 - wet * 0.6);
    material.roughness = dryRoughness + (0.34 - dryRoughness) * wet;
  }
}

export function kerbMaterial(): THREE.MeshStandardMaterial {
  return remember('kerb', () => new THREE.MeshStandardMaterial({
    color: 0xb3b0a8, roughness: 0.9,
  }));
}

/**
 * A window in a room nobody is in.
 *
 * Identical to `windowMaterial` but never brightened at night, so a street
 * shows a mix of lit and dark windows instead of every pane in the
 * neighbourhood switching on together - which is the single loudest way a
 * lit-up town reads as a lighting effect rather than as people being home.
 */
export function darkWindowMaterial(): THREE.MeshStandardMaterial {
  return remember('window-dark', () => {
    // An unlit room is still glass: it reflects the sky exactly as its lit
    // neighbour does, and only the half below the glazing bar goes dark. A
    // flat tint here left a third of every facade reading as painted panels
    // beside windows that had just been given depth.
    const lit = windowMaterial();
    const material = lit.clone();
    material.emissiveMap = null;
    material.emissiveIntensity = 0;
    material.color = new THREE.Color(0x8496a2);
    return material;
  });
}

/**
 * Windows use emissive so lit buildings read clearly at night. The base colour
 * is a real glass blue rather than near-black; dark panes on a lit wall read as
 * holes punched in the geometry (VISUAL_AUDIT V-03).
 */
export function windowMaterial(): THREE.MeshStandardMaterial {
  return remember('window', () => {
    const size = 128;
    // A domestic window, painted. Same reasoning as the shopfront interior
    // below: the wall is a solid box, so there is no room to model behind the
    // pane and a modelled one would be inside the wall. What the eye actually
    // uses to call a rectangle "a window" is that its top half reflects the
    // sky and its bottom half does not - a single flat colour reads as a
    // painted plate no matter what tint it is (VISUAL_REALISM_AUDIT R-08).
    const { c, ctx } = canvas(size);

    // Sky reflection over the upper pane, room behind the lower one.
    const sky = ctx.createLinearGradient(0, 0, 0, size);
    sky.addColorStop(0, '#dcebf7');
    sky.addColorStop(0.42, '#a9c6dd');
    sky.addColorStop(0.46, '#5d6469');
    sky.addColorStop(1, '#41403d');
    ctx.fillStyle = sky;
    ctx.fillRect(0, 0, size, size);

    // The room: a warm back wall and a suggestion of a lamp, dim enough that
    // it only really shows once the emissive comes up after dark.
    const room = ctx.createRadialGradient(
      size * 0.68, size * 0.72, 2, size * 0.68, size * 0.72, size * 0.45,
    );
    room.addColorStop(0, 'rgba(228,190,132,0.7)');
    room.addColorStop(1, 'rgba(150,124,88,0)');
    ctx.fillStyle = room;
    ctx.fillRect(0, size * 0.46, size, size * 0.54);

    // A curtain gathered down one edge. Asymmetry is most of what stops a row
    // of identical panes reading as a repeated stamp.
    const curtain = ctx.createLinearGradient(0, 0, size * 0.3, 0);
    curtain.addColorStop(0, 'rgba(226,214,192,0.85)');
    curtain.addColorStop(1, 'rgba(226,214,192,0)');
    ctx.fillStyle = curtain;
    ctx.fillRect(0, size * 0.46, size * 0.3, size * 0.54);

    // A diagonal specular streak across the upper pane.
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    const streak = ctx.createLinearGradient(0, size * 0.5, size, 0);
    streak.addColorStop(0.32, 'rgba(255,255,255,0)');
    streak.addColorStop(0.5, 'rgba(255,255,255,0.3)');
    streak.addColorStop(0.68, 'rgba(255,255,255,0)');
    ctx.fillStyle = streak;
    ctx.fillRect(0, 0, size, size * 0.46);
    ctx.restore();

    // The glazing bar between upper and lower sash.
    ctx.fillStyle = '#d8d2c6';
    ctx.fillRect(0, size * 0.44, size, size * 0.045);

    // Emissive is painted separately, so that after dark the room glows and
    // the sky reflection does not. Reusing the colour map here would light the
    // brightest part of the pane - the reflection - which is backwards.
    const glow = canvas(size);
    glow.ctx.fillStyle = '#000000';
    glow.ctx.fillRect(0, 0, size, size);
    const lit = glow.ctx.createRadialGradient(
      size * 0.62, size * 0.7, 2, size * 0.62, size * 0.7, size * 0.55,
    );
    lit.addColorStop(0, '#fff0cf');
    lit.addColorStop(0.55, '#b58a4e');
    lit.addColorStop(1, '#000000');
    glow.ctx.fillStyle = lit;
    glow.ctx.fillRect(0, size * 0.46, size, size * 0.54);

    // No explicit colorSpace: not one of the several dozen other canvas
    // textures in this file sets one either, and the whole palette was tuned
    // against that. Tagging this one alone would render it darker than the
    // wall it sits in.
    const map = new THREE.CanvasTexture(c);
    map.anisotropy = 16;
    const emissiveMap = new THREE.CanvasTexture(glow.c);
    emissiveMap.anisotropy = 16;

    return new THREE.MeshStandardMaterial({
      map,
      emissive: 0xffd9a0,
      emissiveMap,
      emissiveIntensity: 0,
      roughness: 0.14,
      metalness: 0.22,
    });
  });
}

/**
 * What you see through a shopfront: a lit interior, painted.
 *
 * Geometry was the first attempt and it cannot work - the building is a solid
 * box, so a recess modelled behind the facade is simply inside the wall, and
 * pushing the glazing out far enough to hold one turns every shop into a
 * conservatory. A painted panel sitting a centimetre behind the glass reads
 * correctly from the street at every angle a player can stand at, and costs
 * one quad.
 */
export function shopInteriorMaterial(): THREE.MeshStandardMaterial {
  return remember('shopinterior', () => {
    const size = 256;
    const { c, ctx } = canvas(size);

    // Dark room, brighter toward the ceiling where the lights are.
    const wash = ctx.createLinearGradient(0, 0, 0, size);
    wash.addColorStop(0, '#4a3f30');
    wash.addColorStop(0.22, '#221d18');
    wash.addColorStop(1, '#14110f');
    ctx.fillStyle = wash;
    ctx.fillRect(0, 0, size, size);

    // Ceiling strip lights.
    ctx.fillStyle = '#ffe6bd';
    for (const x of [size * 0.22, size * 0.5, size * 0.78]) {
      ctx.fillRect(x - size * 0.09, size * 0.05, size * 0.18, size * 0.035);
    }

    // Shelving: racks of stock in muted tones, darker toward the back.
    const stock = ['#8a5a3a', '#3f5f6b', '#6b6a3a', '#7a3f42', '#5a5f6b'];
    for (let rack = 0; rack < 4; rack++) {
      const x = size * (0.08 + rack * 0.235);
      const w = size * 0.19;
      for (let shelf = 0; shelf < 4; shelf++) {
        const y = size * (0.34 + shelf * 0.16);
        ctx.fillStyle = '#4a4239';
        ctx.fillRect(x, y + size * 0.055, w, size * 0.012);
        for (let item = 0; item < 3; item++) {
          ctx.fillStyle = stock[(rack * 4 + shelf + item) % stock.length]!;
          ctx.globalAlpha = 0.9 - shelf * 0.08;
          ctx.fillRect(x + (w / 3) * item + 1, y, w / 3 - 2, size * 0.05);
        }
      }
    }
    ctx.globalAlpha = 1;

    const tex = new THREE.CanvasTexture(c);
    tex.anisotropy = 16;
    // Unlit in practice: black diffuse, so the outdoor sun cannot wash the
    // room out at midday, and the painted image carries its own light. That is
    // also what makes a shop glow after dark without a second material.
    return new THREE.MeshStandardMaterial({
      color: 0x000000,
      roughness: 1,
      emissive: 0xffffff,
      emissiveMap: tex,
      emissiveIntensity: 0.85,
    });
  });
}

/**
 * Shopfront glazing.
 *
 * Separate from the window material because it has to be see-through: the
 * shops now have a lit recess behind the glass, and an opaque pane turns that
 * into a flat blue band again. Metalness is kept low - there is no environment
 * map in this scene, so a metallic pane only goes dark.
 */
export function shopGlassMaterial(): THREE.MeshStandardMaterial {
  return remember('shopglass', () =>
    new THREE.MeshStandardMaterial({
      color: 0x7d9cb4,
      roughness: 0.08,
      metalness: 0.05,
      transparent: true,
      // A sheen, not a pane: any more and the lit interior behind it is fog.
      opacity: 0.1,
      // The lit interior behind it is opaque and already drawn; not writing
      // depth keeps two panes at an angle from fighting each other.
      depthWrite: false,
    }),
  );
}

/** Lamp glass: always slightly glowing so lamps read as lamps in daylight too. */
export function lampMaterial(): THREE.MeshStandardMaterial {
  return remember('lamp', () =>
    new THREE.MeshStandardMaterial({
      color: 0xd8e4ee,
      emissive: 0xffe9b8,
      emissiveIntensity: 0.15,
      roughness: 0.25,
    }),
  );
}

/**
 * Shop signage rendered to a canvas. Uses the system UI font stack, so no font
 * files are shipped and the asset licence register stays empty.
 */
export function signTexture(text: string, colour: number): THREE.Texture {
  const key = `sign:${text}:${colour}`;
  const found = textureCache.get(key);
  if (found) return found;

  const width = 1024;
  const height = 128;
  const c = document.createElement('canvas');
  c.width = width;
  c.height = height;
  const ctx = c.getContext('2d')!;
  ctx.clearRect(0, 0, width, height);

  const hex = `#${colour.toString(16).padStart(6, '0')}`;
  ctx.fillStyle = hex;
  ctx.font = '700 74px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  // Squeeze long names rather than letting them overflow the board.
  const maxWidth = width - 40;
  ctx.fillText(text, width / 2, height / 2 + 4, maxWidth);

  const texture = new THREE.CanvasTexture(c);
  texture.anisotropy = 16;
  textureCache.set(key, texture);
  return texture;
}

/** Road markings drawn as a transparent overlay strip. */
export function roadMarkingTexture(kind: 'centre' | 'crossing'): THREE.Texture {
  const key = `marking:${kind}`;
  const found = textureCache.get(key);
  if (found) return found;

  const c = document.createElement('canvas');
  c.width = 128;
  c.height = 128;
  const ctx = c.getContext('2d')!;
  ctx.clearRect(0, 0, 128, 128);
  ctx.fillStyle = 'rgba(232,226,200,0.85)';

  if (kind === 'centre') {
    // Dashed centre line running along the strip's length.
    for (let y = 12; y < 128; y += 42) ctx.fillRect(52, y, 24, 22);
  } else {
    // Zebra bars.
    for (let x = 8; x < 128; x += 26) ctx.fillRect(x, 6, 14, 116);
  }

  const texture = new THREE.CanvasTexture(c);
  texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
  texture.anisotropy = 16;
  textureCache.set(key, texture);
  return texture;
}

/**
 * A soft radial blob used as a fake contact shadow under props and figures.
 * Nothing in the audit made the world feel more unfinished than everything
 * appearing to hover (VISUAL_AUDIT V-04), and a real shadow map cannot
 * resolve small contact darkening at this scale.
 */
export function contactShadowMaterial(): THREE.MeshBasicMaterial {
  return remember('contact', () => {
    const size = 128;
    const { c, ctx } = canvas(size);
    const gradient = ctx.createRadialGradient(
      size / 2, size / 2, 0, size / 2, size / 2, size / 2,
    );
    gradient.addColorStop(0, 'rgba(0,0,0,0.42)');
    gradient.addColorStop(0.55, 'rgba(0,0,0,0.18)');
    gradient.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, size, size);
    return new THREE.MeshBasicMaterial({
      map: new THREE.CanvasTexture(c),
      transparent: true,
      depthWrite: false,
      fog: true,
    });
  });
}

export function disposeAll(): void {
  for (const m of cache.values()) m.dispose();
  for (const t of textureCache.values()) t.dispose();
  cache.clear();
  textureCache.clear();
}
